/**
 * AudienceSourceSelector.jsx — two radio cards: AddressBook | BYO Webhook.
 *
 * Props:
 *   value:    'addressbook' | 'byo_webhook'
 *   onChange: (value) => void
 *   disabled: boolean (optional)
 *
 * Auto-locks the BYO card when feature flag `audience_byo_webhook` is off.
 * Falls back to AddressBook in that case (calls onChange('addressbook')).
 */

import React, { useEffect } from 'react';
import { Card, Radio, Tag, Button, Tooltip } from 'antd';
import { ContactsOutlined, ApiOutlined, LockFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag, FEATURE_KEYS } from '../../utils/planFeatures';
import './AudienceSourceSelector.css';

const SOURCES = {
  ADDRESSBOOK: 'addressbook',
  BYO: 'byo_webhook',
};

export default function AudienceSourceSelector({ value, onChange, disabled = false }) {
  const navigate = useNavigate();
  const { enabled: byoEnabled, loading } = useFeatureFlag(FEATURE_KEYS.BYO_WEBHOOK);

  // If user previously selected BYO but plan no longer supports it, fall back
  useEffect(() => {
    if (!loading && !byoEnabled && value === SOURCES.BYO) {
      onChange(SOURCES.ADDRESSBOOK);
    }
  }, [loading, byoEnabled, value, onChange]);

  const handleChange = (e) => {
    if (disabled) return;
    onChange(e.target.value);
  };

  return (
    <div className="audience-source-selector">
      <h3 className="ass-title">Choose Audience Source</h3>
      <p className="ass-subtitle">
        Where should the recipients for this campaign come from?
      </p>

      <Radio.Group
        value={value || SOURCES.ADDRESSBOOK}
        onChange={handleChange}
        disabled={disabled}
        className="ass-group"
      >
        {/* Address Book card */}
        <Card
          className={`ass-card ${value === SOURCES.ADDRESSBOOK ? 'ass-card--active' : ''}`}
          onClick={() => !disabled && onChange(SOURCES.ADDRESSBOOK)}
          hoverable
        >
          <div className="ass-card-head">
            <Radio value={SOURCES.ADDRESSBOOK} />
            <ContactsOutlined className="ass-icon ass-icon--blue" />
            <strong>Address Book</strong>
            <Tag color="blue" className="ass-tag">Default</Tag>
          </div>
          <p className="ass-desc">
            Pick from contacts already saved in MessageOS. Best for recurring
            broadcasts to your existing audience.
          </p>
          <ul className="ass-bullets">
            <li>✓ Contacts stored in our DB</li>
            <li>✓ Reusable address books</li>
            <li>✓ No engineering work needed</li>
          </ul>
        </Card>

        {/* BYO Webhook card */}
        <Card
          className={`ass-card ${value === SOURCES.BYO ? 'ass-card--active' : ''} ${!byoEnabled ? 'ass-card--locked' : ''}`}
          onClick={() => byoEnabled && !disabled && onChange(SOURCES.BYO)}
          hoverable={byoEnabled}
        >
          <div className="ass-card-head">
            <Radio value={SOURCES.BYO} disabled={!byoEnabled} />
            <ApiOutlined className="ass-icon ass-icon--orange" />
            <strong>Bring Your Own Webhook</strong>
            {!byoEnabled && (
              <Tooltip title="Available on Enterprise plan">
                <LockFilled className="ass-lock" />
              </Tooltip>
            )}
          </div>
          <p className="ass-desc">
            Stream recipients live from <em>your</em> server. We call your URL,
            paginate it, render the template, and send. Phones never persist
            in our DB.
          </p>
          <ul className="ass-bullets">
            <li>✓ Compliance-friendly (banks, fintech, healthcare)</li>
            <li>✓ Always uses your latest data</li>
            <li>✓ Reusable URL across many campaigns</li>
          </ul>
          {!byoEnabled && (
            <Button
              type="link"
              className="ass-upgrade"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/dashboard/pricing');
              }}
            >
              Upgrade to unlock →
            </Button>
          )}
        </Card>
      </Radio.Group>
    </div>
  );
}

AudienceSourceSelector.SOURCES = SOURCES;
